import pluralize from "pluralize";

import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { getNextThreshold, getPreviousThreshold } from "@rapid/utils/getNextThreshold";

interface ThresholdTrackerProps {
    currentImages: number;
    highlight?: boolean;
    onTrainNowClick?: () => void;
    showTrainNow?: boolean;
}

export function ThresholdTracker({
    currentImages,
    highlight = false,
    onTrainNowClick,
    showTrainNow = false
}: ThresholdTrackerProps) {
    const nextThreshold = getNextThreshold(currentImages);
    const previousThreshold = getPreviousThreshold(currentImages);
    const imagesUntilRetrain = nextThreshold - currentImages;
    const segmentSize = nextThreshold - previousThreshold;
    const segmentProgress =
        segmentSize > 0 ? ((currentImages - previousThreshold) / segmentSize) * 100 : 0;

    if (showTrainNow) {
        return (
            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-brand">
                        <i className="far fa-check-circle mr-1" />
                        Ready for retrain
                    </span>
                    {onTrainNowClick && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onTrainNowClick();
                            }}
                            className="text-xs font-medium text-brand hover:text-brand-hover"
                        >
                            Train Now
                        </button>
                    )}
                </div>
                <div className="bg-brand/15 h-1.5 w-full overflow-hidden rounded-full">
                    <div className="h-full rounded-full bg-brand" style={{ width: "100%" }} />
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
                <span
                    className={twClassNames("text-xs", highlight ? "text-brand" : "text-tertiary")}
                >
                    {imagesUntilRetrain} more {pluralize("image", imagesUntilRetrain)} until next
                    retrain
                </span>
            </div>
            <div
                className={twClassNames(
                    "h-1.5 w-full overflow-hidden rounded-full",
                    highlight ? "bg-brand/15" : "bg-gray-200"
                )}
            >
                <div
                    className={twClassNames(
                        "h-full rounded-full transition-all duration-300",
                        highlight ? "bg-brand" : "bg-gray-400"
                    )}
                    style={{ width: `${Math.min(segmentProgress, 100)}%` }}
                />
            </div>
        </div>
    );
}
